import { useState } from 'react';
import { ApiError, login, register, requestPasswordReset, resetPassword, ssoStartUrl, type AuthProviders, type AuthUser } from '../api';

interface AuthGateProps {
  onAuthenticated: (user: AuthUser) => void;
  providers: AuthProviders;
  /** Token och adress ur återställningslänken, när användaren kommer den vägen. */
  resetContext?: { token: string; email: string };
  /** Felet Microsoft-inloggningen skickade tillbaka till, om den misslyckades. */
  ssoError?: string;
}

/**
 * Inloggningsskärmen. Den visas i stället för applikationen tills det finns en
 * giltig session – all data i verktyget är intern och ska inte ligga öppen.
 */
export function AuthGate({ onAuthenticated, providers, resetContext, ssoError }: AuthGateProps) {
  const showLocal = providers.localAuth;

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <RadarMark />
          <div>
            <h1 className="auth__title">Uppdragsradar</h1>
            <p className="auth__tagline">Vad marknaden efterfrågar – per område och teknik</p>
          </div>
        </div>

        {providers.sso ? (
          <>
            <h2 className="auth__heading">Logga in</h2>
            <p className="auth__lead">Uppdragsradar är ett internt verktyg. Logga in med ditt Microsoft-konto på Xlent.</p>
            {ssoError ? <div className="auth__notice auth__notice--error">{ssoError}</div> : null}
            <a className="auth__submit auth__sso" href={ssoStartUrl}>
              Logga in
            </a>
          </>
        ) : showLocal ? null : (
          <>
            <h2 className="auth__heading">Logga in</h2>
            <div className="auth__notice auth__notice--error">
              {ssoError ?? 'Inloggning är inte konfigurerad. Kontakta en administratör.'}
            </div>
          </>
        )}

        {showLocal ? (
          <>
            {providers.sso ? <div className="auth__divider">eller lokalt konto</div> : null}
            <LocalForm onAuthenticated={onAuthenticated} showHeading={!providers.sso} {...(resetContext ? { resetContext } : {})} />
          </>
        ) : null}
      </div>
    </div>
  );
}

type Mode = 'login' | 'register' | 'forgot' | 'reset';

interface LocalFormProps {
  onAuthenticated: (user: AuthUser) => void;
  resetContext?: { token: string; email: string };
  showHeading: boolean;
}

/** Lokal inloggning är bara tillgänglig när servern sätter LOCAL_AUTH_ENABLED, till exempel vid lokal utveckling. */
function LocalForm({ onAuthenticated, resetContext, showHeading }: LocalFormProps) {
  const [mode, setMode] = useState<Mode>(resetContext ? 'reset' : 'login');
  const [email, setEmail] = useState(resetContext?.email ?? '');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setNotice(undefined);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        onAuthenticated(result.user);
      } else if (mode === 'register') {
        const result = await register(email, password, displayName);
        onAuthenticated(result.user);
      } else if (mode === 'forgot') {
        const result = await requestPasswordReset(email);
        setNotice(result.message);
        setMode('login');
      } else {
        const result = await resetPassword(email, resetContext!.token, password);
        onAuthenticated(result.user);
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Något gick fel. Försök igen.');
    } finally {
      setBusy(false);
    }
  };

  const titles: Record<Mode, string> = {
    login: 'Logga in',
    register: 'Skapa konto',
    forgot: 'Glömt lösenord',
    reset: 'Välj nytt lösenord',
  };

  const leads: Record<Mode, string> = {
    login: 'Uppdragsradar är ett internt verktyg och kräver inloggning.',
    register: 'Du loggas in direkt när kontot är skapat.',
    forgot: 'Ange din adress så skickar vi en återställningslänk.',
    reset: 'Länken gäller en gång. Välj ett lösenord på minst 12 tecken.',
  };

  return (
    <form onSubmit={submit}>
      {showHeading ? (
        <>
          <h2 className="auth__heading">{titles[mode]}</h2>
          <p className="auth__lead">{leads[mode]}</p>
        </>
      ) : null}

      {notice ? <div className="auth__notice auth__notice--ok">{notice}</div> : null}
      {error ? <div className="auth__notice auth__notice--error">{error}</div> : null}

      {mode === 'register' ? (
        <label className="auth__field">
          <span>Namn</span>
          <input
            className="control"
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            required
          />
        </label>
      ) : null}

      <label className="auth__field">
        <span>E-postadress</span>
        <input
          className="control"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          readOnly={mode === 'reset'}
          required
        />
      </label>

      {mode === 'forgot' ? null : (
        <label className="auth__field">
          <span>{mode === 'reset' ? 'Nytt lösenord' : 'Lösenord'}</span>
          <input
            className="control"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={mode === 'login' ? undefined : 12}
            required
          />
          {mode === 'register' || mode === 'reset' ? (
            <small className="auth__hint">Minst 12 tecken. En fras med flera ord är både säkrare och lättare att minnas.</small>
          ) : null}
        </label>
      )}

      <button type="submit" className="auth__submit" disabled={busy}>
        {busy ? 'Ett ögonblick…' : titles[mode]}
      </button>

      <div className="auth__links">
        {mode === 'login' ? (
          <>
            <button type="button" className="link-button" onClick={() => setMode('register')}>
              Skapa konto
            </button>
            <button type="button" className="link-button" onClick={() => setMode('forgot')}>
              Glömt lösenordet?
            </button>
          </>
        ) : mode === 'reset' ? null : (
          <button type="button" className="link-button" onClick={() => setMode('login')}>
            Tillbaka till inloggning
          </button>
        )}
      </div>
    </form>
  );
}

function RadarMark() {
  return (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" stroke="var(--border-strong)" strokeWidth="1.5" />
      <circle cx="16" cy="16" r="8.5" stroke="var(--border-strong)" strokeWidth="1" />
      <path d="M16 16 L16 2 A14 14 0 0 1 28.1 9" fill="var(--accent)" opacity="0.22" />
      <line x1="16" y1="16" x2="16" y2="2" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="22.5" cy="11" r="2.2" fill="var(--series-2)" />
    </svg>
  );
}
